import { useRoute } from '@react-navigation/native';
import React, { useCallback, useMemo } from 'react';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import InviteGuardianContent from '@/features/guardian-player/components/invite-guardian';
import type { RelationshipOption } from '@/features/guardian-player/components/invite-guardian/relationship-selector';
import { useInviteGuardianForm } from '@/features/guardian-player/hooks/use-invite-guardian-form';
import type { InviteGuardianForm } from '@/features/guardian-player/schemas/invite-guardian-schema';
import { calculateAge } from '@/features/guardian-player/utils';
import { formatGuardianApiDate } from '@/features/guardian-player/utils/date';
import { useGuardianStackNavigation } from '@/hooks';
import type { ApiError } from '@/libs/api/types';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { useInvitePlayerGuardianMutation } from '@/queries/use-invite-player-guardian-mutation';
import { PlayerGuardianStatus } from '@/services/get-player-guardians';

const strings = STRINGS.guardianPlayer.inviteGuardian;

/**
 * Tailored invite errors, keyed by the backend HTTP status:
 * - 422 `InvitedAccountNotEligibleError` → no Guardian account for that email
 * - 409 `AlreadyCoGuardianError` → guardian already in the circle
 * Matched by status so nothing in the shared API-error layer needs to change.
 */
const INVITE_ERROR_MESSAGE: Record<number, string> = {
  422: strings.errorNotEligible,
  409: strings.errorAlreadyInCircle,
};

// MOCK_SINCE removed

const ROLE_OPTIONS: RelationshipOption[] = [
  { value: 'parent', label: strings.roles.parent },
  { value: 'grandparent', label: strings.roles.grandparent },
  { value: 'guardian', label: strings.roles.guardian },
  { value: 'other', label: strings.roles.other },
];

const InviteGuardianScreen = () => {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'InviteGuardian'>['route']>();
  const { player } = route.params;

  const { control, handleSubmit } = useInviteGuardianForm();
  // Suppress the shared server-error popup so we can show our own tailored messages.
  const { mutateAsync: invite, isPending } = useInvitePlayerGuardianMutation(player.id, {
    skipServerErrorAlert: true,
  });

  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;
  const screenTitle = strings.screenTitle.replace('{name}', firstName);

  const detailLine = useMemo(() => {
    const age = calculateAge(player.dob);
    const template = age != null ? strings.infoDetailWithAge : strings.infoDetail;
    return template.replace('{age}', String(age)).replace('{level}', String(player.level));
  }, [player.dob, player.level]);

  const sinceLine = useMemo(() => {
    const formatted = formatGuardianApiDate(player.createdOn as string, 'MMM yyyy', '-');
    return strings.infoSince.replace('{date}', formatted);
  }, [player.createdOn]);
  const benefits = useMemo(
    () => [
      strings.benefitSessions.replace('{name}', firstName),
      strings.benefitBooking,
      strings.benefitTournaments,
      strings.benefitBilling,
      strings.benefitCannotRemove,
    ],
    [firstName],
  );

  const onSubmit = useCallback(
    async (form: InviteGuardianForm) => {
      try {
        const newInvite = await invite({
          invitedEmail: form.email.trim(),
          relationship: form.role.toLowerCase(),
          message: form.message.trim() || undefined,
        });

        // Swap this form for the "Invitation Sent" view.
        navigation.replace('InvitationSent', {
          player,
          invitation: {
            ...newInvite,
            invitationCode: newInvite.invitationCode ?? newInvite.id,
            guardianUid: newInvite.guardianUid,
            contact: form.email.trim(),
            relationship: form.role.toLowerCase(),
            status: PlayerGuardianStatus.Waiting,
            expiresAt: newInvite?.expiresAt,
            inviteUrl: newInvite?.inviteUrl,
            message: form.message.trim() || undefined,
          },
        });
      } catch (e) {
        // Map the known invite error statuses to tailored copy; fall back to the server
        // message for anything else. Stay on the screen so the guardian can retry.
        const apiError = e as ApiError;
        const mapped = INVITE_ERROR_MESSAGE[apiError.status];
        showAlert({
          title: STRINGS.common.alert.error,
          message: mapped ?? apiError.message ?? STRINGS.errors.unexpectedError,
        });
      }
    },
    [invite, navigation, player],
  );

  return (
    <InviteGuardianContent
      screenTitle={screenTitle}
      playerName={firstName}
      avatarUrl={player.avatarUrl}
      detailLine={detailLine}
      sinceLine={sinceLine}
      benefits={benefits}
      roleOptions={ROLE_OPTIONS}
      control={control}
      onBack={navigation.goBack}
      onSubmit={handleSubmit(onSubmit)}
      submitting={isPending}
    />
  );
};

export default InviteGuardianScreen;
